import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import "./Grade9MathLesson.css";

// Import your existing tools:
import Calculator from "../MathTools/Calculator";
import WritingPad from "../MathTools/WritingPad";
import defaultProfile from "../../UserProfile/EmptyProfile.jpg";
import { chapterPages } from "./Grade9chapterPages";
// For awarding tokens (like in Grade11 and Grade10):
import { awardToken } from "../../awardtoken";

// Suppose you have these specialized Grade 9 Tools:

import QuadraticGraphTool from "../MathTools/QuadraticGraphTool";
import CircleTool from "../MathTools/CircleTool";
import DataAnalyzer from "../MathTools/DataAnalyzer";
import ShapePreview from "../MathTools/ShapePreview";

/* ----------------------------------------------------------
   1) Chapter definitions and pages (the "slides" of the lesson)
   We break the large text into multiple pages, similarly to Grade8.
---------------------------------------------------------- */
const chapterTopicMap = {
  1: "exponentsRadicals",
  2: "linearSystems",
  3: "quadraticFunctions",
  4: "geometryCircles",
  5: "statsProbability",
  6: null // final summary => no single topic
};


/* ----------------------------------------------------------
   2) getQuestionPool() - A bank of question generators for each topic
   We'll make them more robust, with random numeric variations if possible.
---------------------------------------------------------- */
function getQuestionPool(topic) {
  if (topic === "exponentsRadicals") {
    return [
      // 1) Multiply powers with same base ⇒ add exponents
      {
        question: () => {
          const base = Math.floor(Math.random() * 5) + 2;   // 2..6
          const e1   = Math.floor(Math.random() * 4) + 1;   // 1..4
          const e2   = Math.floor(Math.random() * 4) + 1;   // 1..4
          return {
            q: `Simplify: ${base}^${e1} × ${base}^${e2}`,
            a: `${base}^${e1 + e2}`,
            distract: [
              `${base}^${e1 * e2}`,
              `${base}^${Math.abs(e1 - e2)}`
            ]
          };
        }
      },
  
      // 2) Divide powers with same base ⇒ subtract exponents
      {
        question: () => {
          const base = Math.floor(Math.random() * 5) + 2;   // 2..6
          let       e1   = Math.floor(Math.random() * 5) + 2; // 2..6
          let       e2   = Math.floor(Math.random() * 4) + 1; // 1..4
          if (e2 > e1) [e1, e2] = [e2, e1]; // ensure e1 ≥ e2
          return {
            q: `Simplify: ${base}^${e1} ÷ ${base}^${e2}`,
            a: `${base}^${e1 - e2}`,
            distract: [
              `${base}^${e1 + e2}`,
              `${base}^${e2 - e1}`
            ]
          };
        }
      },
  
      // 3) Power of a power ⇒ multiply exponents
      {
        question: () => {
          const base = Math.floor(Math.random() * 5) + 2;   // 2..6
          const e1   = Math.floor(Math.random() * 3) + 2;   // 2..4
          const e2   = Math.floor(Math.random() * 3) + 2;   // 2..4
          return {
            q: `Simplify: (${base}^${e1})^${e2}`,
            a: `${base}^${e1 * e2}`,
            distract: [
              `${base}^${e1 + e2}`,
              `${base}^${e2 - e1}`
            ]
          };
        }
      },
  
      // 4) **New:** Evaluate a fractional exponent
    {
      question: () => {
        // pick base = perfect power
        const choices = [
          { base: 8,  num: 2, den: 3 },   // 8^(2/3)=4
          { base: 27, num: 2, den: 3 },   // 27^(2/3)=9
          { base: 16, num: 3, den: 4 },   // 16^(3/4)=8
          { base: 32, num: 3, den: 5 }    // 32^(3/5)= (2^5)^(3/5)=2^3=8
        ];
        const pick = choices[Math.floor(Math.random() * choices.length)];
        // compute correct
        const correct = Math.round(Math.pow(pick.base, pick.num / pick.den));
        // distractors
        const d1 = correct * 2;
        const d2 = Math.round(Math.pow(pick.base, pick.num / pick.den + 1));

        return {
          q: `Evaluate: ${pick.base}^(${pick.num}/${pick.den})`,
          a: String(correct),
          distract: [String(d1), String(d2)]
        };
      }
    },

    // 5) **New:** Simplify a non‑perfect radical
    {
      question: () => {
        // choose m so that k^2 * m is in [32, 200]
        const k = Math.floor(Math.random() * 4) + 2;  // 2..5
        const m = Math.floor(Math.random() * 5) + 2;  // 2..6
        const n = k * k * m;
        return {
          q: `Simplify: √${n}`,
          a: `${k}√${m}`,
          distract: [
            `√${n}`, 
            `${k*k}√${m/k}`
          ]
        };
      }
    },
  
      // 6) Simplify a radical (perfect square)
      {
        question: () => {
          const squares = [4, 9, 16, 25, 36, 49, 64, 81, 100];
          const n       = squares[Math.floor(Math.random() * squares.length)];
          return {
            q: `What is √${n}?`,
            a: String(Math.sqrt(n)),
            distract: [
              String(Math.sqrt(n) + 1),
              String(Math.sqrt(n) - 1 > 0 ? Math.sqrt(n) - 1 : Math.sqrt(n) + 2)
            ]
          };
        }
      },
  
      // 7) Factor under the radical: √(k^2·m) ⇒ k√m
      {
        question: () => {
          const k = Math.floor(Math.random() * 4) + 2; // 2..5
          const m = Math.floor(Math.random() * 6) + 2; // 2..7
          const n = k * k * m;
          return {
            q: `Simplify: √${n}`,
            a: `${k}√${m}`,
            distract: [
              `${k * k}√${m / k}`,
              `√${m * k}`
            ]
          };
        }
      },
  
      // 8) Convert radical to fractional exponent
      {
        question: () => {
          const base  = Math.floor(Math.random() * 6) + 2;  // 2..7
          const isCube = Math.random() < 0.4; 
          return {
            q: `Rewrite as an exponent: ${isCube ? '³√' : '√'}(${base})`,
            a: `${base}^(1/${isCube ? 3 : 2})`,
            distract: [
              `${base}^(2/${isCube ? 3 : 2})`,
              `${base}^(-1/${isCube ? 3 : 2})`
            ]
          };
        }
      },
  
      // 9) Multiply fractional exponents
      {
        question: () => {
          const base = Math.floor(Math.random() * 5) + 2;  // 2..6
          const n1   = Math.floor(Math.random() * 3) + 1;  // 1..3
          const d1   = Math.floor(Math.random() * 3) + 2;  // 2..4
          const n2   = Math.floor(Math.random() * 3) + 1; 
          const d2   = Math.floor(Math.random() * 3) + 2; 
          const top  = n1 * d2 + n2 * d1;
          const bot  = d1 * d2;
          return {
            q: `Simplify: ${base}^(${n1}/${d1}) × ${base}^(${n2}/${d2})`,
            a: `${base}^(${top}/${bot})`,
            distract: [
              `${base}^(${n1 * n2}/${d1 * d2})`,
              `${base}^(${Math.abs(n1 - n2)}/${d1 * d2})`
            ]
          };
        }
      },
  
      // 10) Simplify a quotient of powers ⇒ subtract exponents
      {
        question: () => {
          const base = Math.floor(Math.random() * 5) + 2;   // 2..6
          let   e1   = Math.floor(Math.random() * 5) + 2;   // 2..6
          let   e2   = Math.floor(Math.random() * 4) + 1;   // 1..4
          if (e2 > e1) [e1, e2] = [e2, e1];
          return {
            q: `Simplify: ${base}^${e1} / ${base}^${e2}`,
            a: `${base}^${e1 - e2}`,
            distract: [
              `${base}^${e1 + e2}`,
              `${base}^${e2 - e1}`
            ]
          };
        }
      }
    ];
  }
  
  else if (topic === "linearSystems") {
    return [
  
      // 1) Elimination with random x,y
      {
        question: () => {
          // pick an integer solution 
          const x = Math.floor(Math.random() * 5) + 1; // 1..5
          const y = Math.floor(Math.random() * 5) + 1; // 1..5
          // random coefficients ensuring nonzero determinant
          let a1, b1, a2, b2;
          do {
            a1 = Math.floor(Math.random() * 5) + 1;
            b1 = Math.floor(Math.random() * 5) + 1;
            a2 = Math.floor(Math.random() * 5) + 1;
            b2 = Math.floor(Math.random() * 5) + 1;
          } while (a1 * b2 === a2 * b1);
          // compute RHS so that (x,y) is solution
          const c1 = a1 * x + b1 * y;
          const c2 = a2 * x + b2 * y;
  
          return {
            q: `Solve by elimination:\n` +
               `${a1}x + ${b1}y = ${c1}\n` +
               `${a2}x + ${b2}y = ${c2}`,
            a: `x = ${x}, y = ${y}`,
            distract: [
              `x = ${y}, y = ${x}`,
              `x = ${(c1 + c2)}/${(a1 + a2)}, y = ${(c1 - c2)}/${(b1 - b2)}`
            ]
          };
        }
      },
  
      // 2) Substitution from slope-intercept + second eq
      {
        question: () => {
          const m  = Math.floor(Math.random() * 4) + 1;   // slope 1..4
          const b  = Math.floor(Math.random() * 6) - 2;   // intercept -2..3
          const a2 = Math.floor(Math.random() * 4) + 1;   // 1..4
          const y2 = Math.floor(Math.random() * 5) + 1;   // pick y for solution
          // choose x so that everything stays integer
          // from y = m x + b => x = (y2 - b)/m
          const x2 = Math.floor((y2 - b) / m);
          const c2 = a2 * x2 + y2;
  
          const fmt = v => Number.isInteger(v) ? v : v.toFixed(1);
  
          return {
            q: `Solve by substitution:\n` +
               `y = ${m}x ${b>=0?'+':'-'}${Math.abs(b)}\n` +
               `${a2}x + y = ${c2}`,
            a: `x = ${fmt(x2)}, y = ${fmt(y2)}`,
            distract: [
              `x = ${fmt(y2)}, y = ${fmt(x2)}`,
              `x = ${fmt(x2 + 1)}, y = ${fmt(y2 - 1)}`
            ]
          };
        }
      },
  
      // 3) Unique vs none vs infinite: ask # of solutions for random slopes
      {
        question: () => {
          // randomly choose one of the three cases
          const caseType = Math.random();
          if (caseType < 0.33) {
            // exactly one solution
            let m1, m2;
            do {
              m1 = Math.floor(Math.random() * 5) + 1;
              m2 = Math.floor(Math.random() * 5) + 1;
            } while (m1 === m2);
            const b1 = Math.floor(Math.random() * 7) - 3;
            const b2 = Math.floor(Math.random() * 7) - 3;
            return {
              q: `How many solutions?\n` +
                 `y = ${m1}x ${b1>=0?'+':'-'}${Math.abs(b1)}\n` +
                 `y = ${m2}x ${b2>=0?'+':'-'}${Math.abs(b2)}`,
              a: "Exactly one solution",
              distract: ["No solution", "Infinitely many solutions"]
            };
          } else if (caseType < 0.66) {
            // no solution
            const m = Math.floor(Math.random() * 5) + 1;
            const b = Math.floor(Math.random() * 7) - 3;
            return {
              q: `How many solutions?\n` +
                 `y = ${m}x ${b>=0?'+':'-'}${Math.abs(b)}\n` +
                 `y = ${m}x ${b+Math.floor(Math.random()*4)+1>=0?'+':''}${b + Math.floor(Math.random()*4)+1}`,
              a: "No solution",
              distract: ["Exactly one solution", "Infinitely many solutions"]
            };
          } else {
            // infinitely many
            const m = Math.floor(Math.random() * 5) + 1;
            const b = Math.floor(Math.random() * 7) - 3;
            const k = Math.floor(Math.random() * 4) + 2;
            return {
              q: `How many solutions?\n` +
                 `y = ${m}x ${b>=0?'+':'-'}${Math.abs(b)}\n` +
                 `${k}y = ${k*m}x ${k*b>=0?'+':''}${k*b}`,
              a: "Infinitely many solutions",
              distract: ["Exactly one solution", "No solution"]
            };
          }
        }
      },
  
      // 4) Simple “how many?” special case: parallel vertical/horizontal
      {
        question: () => {
          const isVertical = Math.random() < 0.5;
          if (isVertical) {
            const x0 = Math.floor(Math.random() * 5) + 1;
            return {
              q: `How many solutions?\n` +
                 `x = ${x0}\n` +
                 `x = ${x0 + Math.floor(Math.random()*3)+1}`,
              a: "No solution",
              distract: ["Exactly one solution", "Infinitely many solutions"]
            };
          } else {
            const y0 = Math.floor(Math.random() * 5) + 1;
            return {
              q: `How many solutions?\n` +
                 `y = ${y0}\n` +
                 `y = ${y0}`,
              a: "Infinitely many solutions",
              distract: ["No solution", "Exactly one solution"]
            };
          }
        }
      },
  
      // 5) Word problem with two item prices
      {
        question: () => {
          // random integer prices
          const p = Math.floor(Math.random() * 5) + 1;   // $1..$5
          const n = Math.floor(Math.random() * 5) + 1;   // $1..$5
          // random quantities
          const a1 = Math.floor(Math.random() * 4) + 2;  // 2..5
          const b1 = Math.floor(Math.random() * 4) + 1;  // 1..4
          const a2 = Math.floor(Math.random() * 3) + 1;  // 1..3
          const b2 = Math.floor(Math.random() * 3) + 1;  // 1..3
          const cost1 = (a1 * p + b1 * n).toFixed(2);
          const cost2 = (a2 * p + b2 * n).toFixed(2);
  
          return {
            q: `In a store:\n` +
               `Customer A buys ${a1} pens and ${b1} notebooks for $${cost1}.\n` +
               `Customer B buys ${a2} pens and ${b2} notebooks for $${cost2}.\n` +
               `Let p = price of a pen, n = price of a notebook. Solve for p and n.`,
            a: `p = $${p.toFixed(2)}, n = $${n.toFixed(2)}`,
            distract: [
              `p = $${n.toFixed(2)}, n = $${p.toFixed(2)}`,
              `p = $${(p+1).toFixed(2)}, n = $${(n-1>0?n-1:n+2).toFixed(2)}`
            ]
          };
        }
      },
  
      // 6) Graph intercepts → write equations
      {
        question: () => {
          const xInt = Math.floor(Math.random() * 5) + 1; // 1..5
          const yInt = Math.floor(Math.random() * 5) + 1; // 1..5
          return {
            q: `A line crosses the x-axis at (${xInt},0) and the y-axis at (0,${yInt}).\n` +
               `Write its equation in slope-intercept form.`,
            a: `y = ${-yInt/xInt}x + ${yInt}`,
            distract: [
              `y = ${yInt/xInt}x + ${yInt}`,
              `y = ${-yInt/xInt}x - ${yInt}`
            ]
          };
        }
      }
  
    ];
  }
  
  else if (topic === "quadraticFunctions") {
    return [
      // 1) Factor and read roots from graph
      {
        question: () => {
          const pairs = [
            { r1: 2, r2: 3 },
            { r1: 1, r2: 4 },
            { r1: 3, r2: 4 },
            { r1: 2, r2: 5 },
            { r1: 1, r2: 6 }
          ];
          const { r1, r2 } = pairs[Math.floor(Math.random() * pairs.length)];
          const b = -(r1 + r2);
          const c = r1 * r2;
          const rule = `y = x^2 ${b<0?b:`+${b}`}x ${c<0?c:`+${c}`}`;
          return {
            q: `Factor and find the roots of\n${rule}`,
            visual: (
              <QuadraticGraphTool
                rule={rule}
                widthCells={12}
                heightCells={8}
              />
            ),
            a: `Roots: ${r1} and ${r2}`,
            distract: [
              `Roots: -${r1} and -${r2}`,
              `Roots: ${r1+1} and ${r2-1}`
            ]
          };
        }
      },
  
      // 2) Identify vertex from a vertex‐form graph
      {
        question: () => {
          const h = Math.floor(Math.random() * 5); // 0..4
          const k = Math.floor(Math.random() * 5); // 0..4
          const rule = `y = (x - ${h})^2 + ${k}`;
          return {
            q: `What is the vertex of this parabola?`,
            visual: (
              <QuadraticGraphTool
                rule={rule}
                widthCells={12}
                heightCells={8}
              />
            ),
            a: `(${h}, ${k})`,
            distract: [
              `(-${h}, ${k})`,
              `(${h}, -${k})`
            ]
          };
        }
      },
  
      // 3) Opening direction from leading coefficient
      {
        question: () => {
          const a  = Math.floor(Math.random() * 5) + 1;  // 1..5
          const neg = Math.random() < 0.5;
          const A  = neg ? -a : a;
          const rule = `y = ${A}x^2 + 5x - 2`;
          return {
            q: `Does this parabola open up or down?`,
            visual: (
              <QuadraticGraphTool
                rule={rule}
                widthCells={12}
                heightCells={8}
              />
            ),
            a: neg ? "Downward" : "Upward",
            distract: [
              neg ? "Upward" : "Downward",
              "Neither — it’s not a parabola"
            ]
          };
        }
      },
  
      // 4) Evaluate at a given x (with small graph for context)
      {
        question: () => {
          const a = Math.floor(Math.random() * 3) + 1;  // 1..3
          const b = Math.floor(Math.random() * 5) - 2;  // -2..2
          const c = Math.floor(Math.random() * 5);      // 0..4
          const x = Math.floor(Math.random() * 5);      // 0..4
          const val = a*x*x + b*x + c;
          const rule = `y = ${a}x^2 ${b>=0?`+${b}`:`${b}`}x ${c>=0?`+${c}`:`${c}`}`;
          return {
            q: `Evaluate this function at x = ${x}:`,
            visual: (
              <QuadraticGraphTool
                rule={rule}
                widthCells={12}
                heightCells={8}
              />
            ),
            a: String(val),
            distract: [
              String(val + 1),
              String(val - 1)
            ]
          };
        }
      },
  
      // 5) Maximum point in a real‐world path
      {
        question: () => {
          const v0 = Math.floor(Math.random() * 6) + 10; // 10..15
          const rule = `h(t) = -5t^2 + ${v0}t + 1`;
          // vertex at t = -b/(2a) = v0/(10)
          const tMax = (v0 / 10).toFixed(1);
          return {
            q: `A ball’s height is given by:\n${rule}\n` +
               `At what time (seconds) does it reach its maximum?`,
            visual: (
              <QuadraticGraphTool
                rule={rule}
                widthCells={12}
                heightCells={8}
              />
            ),
            a: `${tMax} s`,
            distract: [
              `${(tMax * 2).toFixed(1)} s`,
              "0 s"
            ]
          };
        }
      }
    ];
  }
  

  else if (topic === "geometryCircles") {
    return [
      // 1) Circumference from radius
      {
        question: () => {
          const r = Math.floor(Math.random() * 5) + 2; // 2..6
          const approx = (2 * Math.PI * r).toFixed(1);
          return {
            q: `A circle has radius ${r} cm. Approximate its circumference (π≈3.14).`,
            visual: <CircleTool radius={r} showMeasurements={["circumference"]} />,
            a: `${approx} cm`,
            distract: [
              `${(Math.PI * r).toFixed(1)} cm`,
              `${(2 * Math.PI * (r + 1)).toFixed(1)} cm`
            ]
          };
        }
      },
  
      // 2) Area from diameter
      {
        question: () => {
          const d = Math.floor(Math.random() * 5) + 4; // 4..8
          const r = d / 2;
          const approx = (Math.PI * r * r).toFixed(1);
          return {
            q: `A circle has diameter ${d} m. Approximate its area (π≈3.14).`,
            visual: <CircleTool diameter={d} showMeasurements={["area"]} />,
            a: `${approx} m²`,
            distract: [
              `${(Math.PI * d).toFixed(1)} m²`,
              `${(2 * Math.PI * r).toFixed(1)} m²`
            ]
          };
        }
      },
  
      // 3) Diameter from circumference
      {
        question: () => {
          const diam = Math.floor(Math.random() * 10) + 5; // 5..14
          const circ = (Math.PI * diam).toFixed(1);
          return {
            q: `A circle’s circumference is ${circ} units. What is its diameter (π≈3.14)?`,
            visual: <CircleTool diameter={diam} showMeasurements={["circumference"]} />,
            a: String(diam),
            distract: [
              String(diam + 2),
              String(Math.floor(diam / 2))
            ]
          };
        }
      },
  
      // 4) Effect of doubling radius
      {
        question: () => {
          const r1 = 3;
          const r2 = 6;
          return {
            q: "If the radius of a circle is doubled, how does the circumference and area change?",
            visual: (
              <div style={{ display: "flex", gap: 40, justifyContent: "center" }}>
                <div style={{ textAlign: "center" }}>
                  <ShapePreview shape={{ type: "circle", radius: r1 }} />
                  <p>Radius = {r1}</p>
                </div>
                <div style={{ textAlign: "center" }}>
                  <ShapePreview shape={{ type: "circle", radius: r2 }} />
                  <p>Radius = {r2}</p>
                </div>
              </div>
            ),
            a: "Circumference doubles; area quadruples",
            distract: [
              "Circumference quadruples; area doubles",
              "Both circumference and area double"
            ]
          };
        }
      },
  
      // 5) Central angle of pizza slices
      {
        question: () => ({
          q: "A pizza is cut into 8 equal slices. What is the central angle of each slice?",
          visual: <CircleTool radius={4} slices={8} showMeasurements={["angle"]} />,
          a: "45°",
          distract: ["60°", "30°"]
        })
      }
    ];
  }
  
  else if (topic === "statsProbability") {
    return [
      // 1) Combined mean, median, mode, range on a 6‑element set
      {
        question: () => {
          const base = Array.from({ length: 5 }, () =>
            Math.floor(Math.random() * 20) + 1
          );
          const dupIdx = Math.floor(Math.random() * 5);
          const arr = [...base, base[dupIdx]];    // 6 elements, guaranteed mode
          const sum = arr.reduce((a, b) => a + b, 0);
          const mean = (sum / arr.length).toFixed(1);
          const sorted = [...arr].sort((a, b) => a - b);
          const median = sorted[Math.floor(sorted.length / 2)];
          const freq = {};
          arr.forEach(v => (freq[v] = (freq[v] || 0) + 1));
          const maxFreq = Math.max(...Object.values(freq));
          const modes = Object.keys(freq).filter(k => freq[k] === maxFreq).join(", ");
          const range = sorted[sorted.length - 1] - sorted[0];
          return {
            q: `Data set: [${arr.join(", ")}]. Find the mean (1‑decimal), median, mode, and range.`,
            visual: (
              <DataAnalyzer
                initialData={arr}
                initialView="histogram"
                showStats={["mean","median","mode","range"]}
              />
            ),
            a: `Mean=${mean}, Median=${median}, Mode=${modes}, Range=${range}`,
            distract: [
              `Mean=${(parseFloat(mean)+1).toFixed(1)}, Median=${median}, Mode=${modes}, Range=${range}`,
              `Mean=${mean}, Median=${median}, Mode=${modes}, Range=${range+2}`
            ]
          };
        }
      },
  
      // 2) Mode only (possibly multi‑modal)
      {
        question: () => {
          const arr = Array.from({ length: 7 }, () =>
            Math.floor(Math.random() * 5) + 1
          );
          // force a tie by duplicating two different values
          arr.push(arr[0], arr[1]);
          const freq = {};
          arr.forEach(v => (freq[v] = (freq[v] || 0) + 1));
          const maxF = Math.max(...Object.values(freq));
          const modes = Object.keys(freq).filter(k => freq[k] === maxF).join(", ");
          return {
            q: `Data set: [${arr.join(", ")}]. What is/are the mode(s)?`,
            visual: (
              <DataAnalyzer
                initialData={arr}
                initialView="bar"
                showStats={["mode"]}
              />
            ),
            a: modes,
            distract: [
              String(arr[0]),
              String(arr[1])
            ]
          };
        }
      },
  
      // 3) Range only
      {
        question: () => {
          const arr = Array.from({ length: 6 }, () =>
            Math.floor(Math.random() * 30) + 1
          );
          const sorted = [...arr].sort((a, b) => a - b);
          const range = sorted[sorted.length - 1] - sorted[0];
          return {
            q: `Data set: [${arr.join(", ")}]. Find the range.`,
            a: String(range),
            distract: [
              String(range + 2),
              String(Math.abs(range - 2))
            ]
          };
        }
      },
  
      // 4) “At least one head” in 4 coin flips
      {
        question: () => {
          const n = 4;
          const pNone = Math.pow(0.5, n);
          const pAtLeastOne = (1 - pNone).toFixed(3);
          return {
            q: `If you flip ${n} fair coins, what's the probability of getting at least one head?`,
            visual: (
              <DataAnalyzer
                initialData={[
                  ...Array(1).fill(`${n} tails`),
                  ...Array((Math.pow(2, n) - 1)).fill(`≥1 head`)
                ]}
                categories={[`${n} tails`, "≥1 head"]}
                initialView="bar"
                showProbabilities={true}
              />
            ),
            a: `1 − (1/2)^${n} = ${pAtLeastOne}`,
            distract: [
              (pNone).toFixed(3),
              `1/${n}`
            ]
          };
        }
      },
  
      // 5) Union probability: even OR >4 on a die
      {
        question: () => ({
          q: `You roll one fair six‑sided die. What is P(even OR >4)?`,
          visual: (
            <DataAnalyzer
              initialData={[1,2,3,4,5,6]}
              categories={[1,2,3,4,5,6]}
              initialView="bar"
              showProbabilities={true}
            />
          ),
          a: `(3 evens + 2 above 4 − 1 overlap)/6 = 4/6 = 2/3`,
          distract: ["3/6","5/6"]
        })
      },
  
      // 6) Conditional probability (red marble given previous red, with replacement)
      {
        question: () => ({
          q: `A jar has 3 red and 2 blue marbles. You draw one, replace it, then draw again. What is P(red on 2nd draw | red on 1st)?`,
          a: `3/5 (replacement ⇒ independent)`,
          distract: ["(3/5)*(3/5)","2/5"]
        })
      },
  
      // 7) No‑replacement probability of mixed color
      {
        question: () => {
          const R = 4, B = 3;
          return {
            q: `A jar has ${R} red and ${B} blue marbles. You draw two without replacing. What is P(one red & one blue)?`,
            a: `${R/ (R+B)} * ${B/(R+B-1)} + ${B/(R+B)} * ${R/(R+B-1)} = ${( (R/(R+B))*(B/(R+B-1)) *2 ).toFixed(3)}`,
            distract: ["2/7","1/2"]
          };
        }
      },
  
      // 8) Binomial probability: exactly 2 successes in 5 trials (p=0.3)
      {
        question: () => {
          const n = 5, k = 2, p = 0.3;
          const comb = (n, k) => {
            let res = 1;
            for (let i=0; i<k; i++) res *= (n - i)/(i+1);
            return res;
          };
          const prob = (comb(n,k) * Math.pow(p,k) * Math.pow(1-p, n-k)).toFixed(3);
          return {
            q: `In a binomial model with n=${n}, p=${p}, what's P(X=${k})?`,
            a: `${prob}`,
            distract: [
              (prob*2).toFixed(3),
              (prob/2).toFixed(3)
            ]
          };
        }
      },
  
      // 9) Experimental probability via DataAnalyzer
      {
        question: () => {
          const trials = 20;
          return {
            q: `Simulate ${trials} coin flips. Which bar heights (frequencies) would you expect for H/T?`,
            visual: (
              <DataAnalyzer
                simulate={{ type: "coin", trials }}
                initialView="bar"
                showStats={["frequency"]}
              />
            ),
            a: `About ${trials/2} heads and ${trials/2} tails`,
            distract: ["All heads","All tails"]
          };
        }
      },
  
      // 10) Permutations: in how many ways can 4 students line up?
      {
        question: () => ({
          q: `In how many ways can 4 distinct students line up in a row?`,
          a: `4! = 24`,
          distract: ["4^4","12"]
        })
      }
    ];
  }
  else {
    console.warn(`No question pool for topic "${topic}"`);
    return [];
  }
}  
  

/* ----------------------------------------------------------
   3) generateQuizQuestion(forcedTopic?) 
   - if forcedTopic is given, pick from that topic.
   - if not, pick from random among the five major topics
---------------------------------------------------------- */
function generateQuizQuestion(forcedTopic) {
  const topics = forcedTopic
    ? [forcedTopic]
    : ["exponentsRadicals","linearSystems","quadraticFunctions","geometryCircles","statsProbability"];
  const chosen = topics[Math.floor(Math.random() * topics.length)];
  const pool   = getQuestionPool(chosen);
  const item   = pool[Math.floor(Math.random() * pool.length)];
  const qObj   = item.question();
  const distractors = qObj.distract || [];
  // randomize options
  const options = [qObj.a, ...distractors].sort(()=>Math.random()-0.5);

  return {
    question: qObj.q,
    correctAnswer: qObj.a,
    options,
    visual: qObj.visual
  };
}

/* ----------------------------------------------------------
   The main Grade9MathLesson component
---------------------------------------------------------- */
export default function Grade9MathLesson(onAwardTokens) {
  // 1) Chapter states
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);

  // 2) Chapter quiz states
  const [showChapterQuiz, setShowChapterQuiz] = useState(false);
  const [chapterQuiz, setChapterQuiz] = useState([]);
  const [chQidx, setChQidx] = useState(0);
  const [chScore, setChScore] = useState(0);
  const [chFin, setChFin] = useState(false);

  // 3) Overall progress
  const [allChaptersDone, setAllChaptersDone] = useState(false);

  // NEW: track if user has done it before
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);
  useEffect(() => {
    // On mount, check localStorage for "grade9MathComplete"
    const completedBefore = localStorage.getItem("grade9MathComplete");
    if (completedBefore === "true") {
      setHasCompletedBefore(true);
      setAllChaptersDone(true); // they previously finished everything
    }
  }, []);

  useEffect(() => {
    // Once user *really* finishes, store that so next time it says "completed"
    if (allChaptersDone) {
      localStorage.setItem("grade9MathComplete", "true");
    }
  }, [allChaptersDone]);

  // 4) Final quiz states
  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fDone, setFDone] = useState(false);

  // Keep a ref so we only award tokens once
  const hasAwardedTokens = useRef(false);

  // 5) UI toggles
  const [showCalc, setShowCalc] = useState(false);
  const [showPad, setShowPad]   = useState(false);

  // 6) user (avatar, etc.)
  const [user, setUser] = useState(null);
  useEffect(() => {
    const logged = JSON.parse(localStorage.getItem("loggedInUser"));
    if (logged) setUser(logged);
  }, []);

  // Gather the pages for the current chapter
  const pages = chapterPages[chapter];
  const currentPage = pages[pageIdx];
  const isLastPage = pageIdx === pages.length - 1;

  // total slides
  const totalSlides = useMemo(() => {
    return Object.values(chapterPages).reduce((acc, arr) => acc + arr.length, 0);
  }, []);
  // slides completed so far
  const slidesSoFar = useMemo(() => {
    let sum = 0;
    for (let c = 1; c < chapter; c++) {
      sum += chapterPages[c].length;
    }
    sum += pageIdx;
    return sum;
  }, [chapter, pageIdx]);
  const progress = Math.round(((slidesSoFar + 1) / totalSlides) * 100);

  /* ------------------------------------------
     Navigation between pages
  ------------------------------------------ */
  function nextPage() {
    if (pageIdx < pages.length - 1) {
      setPageIdx(pageIdx + 1);
    }
  }
  function prevPage() {
    if (pageIdx > 0) {
      setPageIdx(pageIdx - 1);
    }
  }

  /* ------------------------------------------
     Chapter Quiz
  ------------------------------------------ */
  function startChapterQuiz() {
    const topic = chapterTopicMap[chapter];
    if (!topic) {
      // chapter=6 => summary => no single topic => final
      setAllChaptersDone(true);
      return;
    }
    // build 10-question quiz from relevant topic
    const quizArr = [];
    for (let i=0; i<10; i++){
      quizArr.push(generateQuizQuestion(topic));
    }
    setChapterQuiz(quizArr);
    setChQidx(0);
    setChScore(0);
    setChFin(false);
    setShowChapterQuiz(true);
  }
  function handleChAnswer(opt) {
    if (String(opt) === String(chapterQuiz[chQidx].correctAnswer)) {
      setChScore(s=>s+1);
    }
    if (chQidx < chapterQuiz.length -1) {
      setChQidx(i=>i+1);
    } else {
      setChFin(true);
    }
  }
  function finishChapterQuiz() {
    // pass if >=6/10
    if (chScore >= 6) {
      if (chapter < 6) {
        setChapter(c=>c+1);
        setPageIdx(0);
      } else {
        setAllChaptersDone(true);
      }
      setShowChapterQuiz(false);
    } else {
      alert("Score below 60%. Please review and try again!");
      startChapterQuiz();
    }
  }

  /* ------------------------------------------
     Final Quiz
  ------------------------------------------ */
  function startFinalQuiz() {
    // 50 questions from all topics
    const used = new Set();
    const finalArr = [];
    while (finalArr.length < 50) {
      const q = generateQuizQuestion(); // no forcedTopic => random among all
      const key = q.question + "||" + q.correctAnswer;
      if (!used.has(key)) {
        used.add(key);
        finalArr.push(q);
      }
    }
    setFinalQs(finalArr);
    setFIdx(0);
    setFScore(0);
    setFDone(false);
    setShowFinal(true);
  }
  function handleFinalAns(opt){
    if (String(opt) === String(finalQs[fIdx].correctAnswer)) {
      setFScore(s=>s+1);
    }
    if (fIdx < finalQs.length-1) {
      setFIdx(i=>i+1);
    } else {
      setFDone(true);
    }
  }

  /* ------------------------------------------
     Sub-UI Components
  ------------------------------------------ */
  function LessonUI() {
    return (
      <>
        <h2 className="lesson-page-title">{currentPage.title}</h2>
        <div className="lesson-page-content">
          {currentPage.body}
        </div>

        <div className="lesson-page-navigation">
          {pageIdx > 0 && (
            <button className="lesson-nav-btn" onClick={prevPage}>
              ◀ Previous
            </button>
          )}
          {pageIdx < pages.length - 1 && (
            <button className="lesson-nav-btn" onClick={nextPage}>
              Next ▶
            </button>
          )}
        </div>

        {isLastPage && (
          <button className="lesson-nav-btn" onClick={startChapterQuiz}>
            Take Chapter {chapter} Quiz
          </button>
        )}

        <br/>
        <Link to="/math" className="grade9-back">← Back to Math Page</Link>
      </>
    );
  }

  function ChapterQuizUI() {
    if (!chapterQuiz[chQidx]) return <p>Loading quiz...</p>;
    if (!chFin) {
      // quiz in progress
      const q = chapterQuiz[chQidx];
      return (
        <div className="quick-quiz-story-mode">
          <div className="quiz-top-panel">
            <div className="teacher-image-container">
              <img
                src="/images/Teacher.png"
                alt="Teacher"
                className="quiz-character-img"
              />
            </div>
            <div className="quiz-question-box">
              <p className="quiz-question-text">
                Q {chQidx + 1}/{chapterQuiz.length}: <span dangerouslySetInnerHTML={{__html:q.question}}/>
              </p>
              {q.visual && <div style={{ marginTop: 10 }}>{q.visual}</div>}
            </div>
          </div>

          <div className="quiz-bottom-panel">
            <div className="quiz-answer-box-left">
              {q.options.map((opt,i)=>(
                <button
                  key={i}
                  className="quiz-answer-btn"
                  onClick={()=>handleChAnswer(opt)}
                  dangerouslySetInnerHTML={{__html: opt}}
                />
              ))}
              <p className="quiz-score-display">
                Score: {chScore}/{chapterQuiz.length}
              </p>
              <button className="button1" onClick={()=>setShowCalc(c=>!c)}>
                {showCalc ? "Hide Calculator" : "Show Calculator"}
              </button>
              <button className="button1" onClick={()=>setShowPad(p=>!p)}>
                {showPad ? "Hide Writing Pad" : "Show Writing Pad"}
              </button>
              {showCalc && <Calculator />}
              {showPad && <WritingPad />}
            </div>
            <div className="student-image-container">
              <img
                src={user?.avatar || defaultProfile}
                alt="Student"
                className="quiz-character-img-student"
              />
            </div>
          </div>
        </div>
      );
    } else {
      // quiz finished
      return (
        <div className="quiz-result-screen">
          <h3>Chapter {chapter} Quiz Finished!</h3>
          <p>You scored {chScore}/{chapterQuiz.length}.</p>
          <button className="quiz-result-btn" onClick={finishChapterQuiz}>
            Continue
          </button>
        </div>
      );
    }
  }

  function FinalQuizUI() {
    if (!finalQs[fIdx]) return <p>Loading final quiz...</p>;
    if (!fDone) {
      // final quiz in progress
      const q = finalQs[fIdx];
      return (
        <div className="quick-quiz-story-mode">
          <div className="quiz-top-panel">
            <div className="teacher-image-container">
              <img
                src="/images/teacher.png"
                alt="Teacher"
                className="quiz-character-img"
              />
            </div>
            <div className="quiz-question-box">
              <p className="quiz-question-text">
                Q {fIdx + 1}/{finalQs.length}:{" "}
                <span dangerouslySetInnerHTML={{__html: q.question}}/>
              </p>
              {q.visual && <div style={{ marginTop: 10 }}>{q.visual}</div>}
            </div>
          </div>

          <div className="quiz-bottom-panel">
            <div className="quiz-answer-box-left">
              {q.options.map((opt,i)=>(
                <button
                  key={i}
                  className="quiz-answer-btn"
                  onClick={()=>handleFinalAns(opt)}
                  dangerouslySetInnerHTML={{__html: opt}}
                />
              ))}
              <p className="quiz-score-display">
                Score: {fScore}/{finalQs.length}
              </p>
              <button className="button1" onClick={()=>setShowCalc(c=>!c)}>
                {showCalc ? "Hide Calculator" : "Show Calculator"}
              </button>
              <button className="button1" onClick={()=>setShowPad(p=>!p)}>
                {showPad ? "Hide Writing Pad" : "Show Writing Pad"}
              </button>
              {showCalc && <Calculator />}
              {showPad && <WritingPad />}
            </div>
            <div className="student-image-container">
              <img
                src={user?.avatar || defaultProfile}
                alt="Student"
                className="quiz-character-img-student"
              />
            </div>
          </div>
        </div>
      );
    } else {
      // final quiz finished
       // final quiz done => possibly award token if >=80%
       if (!hasAwardedTokens.current) {
        const percent = (fScore / finalQs.length) * 100;
        if (percent >= 80) {
          awardToken();  // legacy—updates localStorage (if needed by other code)
          if (onAwardTokens) onAwardTokens(1);  // this notifies App.js to increment navbar!
          const updated = JSON.parse(localStorage.getItem("loggedInUser"));
          setUser(updated);
        }
        const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
        quizResults["math_g9"] = {
            ...(quizResults["math_g9"] || {}),
              finalQuizScore: fScore,
                finalQuizTotal: finalQs.length,
                  lastCompleted: Date.now()
                };
        localStorage.setItem("quizResults", JSON.stringify(quizResults));
        hasAwardedTokens.current = true;
      }

      return (
        <div className="quiz-result-screen">
          <h3>Final Quiz Finished!</h3>
          <p>You scored {fScore}/{finalQs.length}!</p>
          <button className="quiz-result-btn" onClick={()=>setShowFinal(false)}>
            Return to Lesson
          </button>
        </div>
      );
    }
  }

  /* ------------------------------------------
     Main Render
  ------------------------------------------ */
  return (
    <div className="grade9-math-page">
      <div className="grade9-math-page-scale">
        {/* Progress bar */}
        <div style={{ margin: "10px 0" }}>
          <div
            style={{
              width: "80%",
              margin: "0 auto",
              background: "#ddd",
              borderRadius: 10,
              height: 20
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                background: "#4caf50",
                height: 20,
                transition: "width 0.3s"
              }}
            />
          </div>
          <p>{progress}% complete</p>
        </div>
        {hasCompletedBefore && !showFinal && !showChapterQuiz && !allChaptersDone && (
        <div style={{ textAlign: "center", margin: "15px 0", color: "#555" }}>
          <p>
          You previously completed the entire Grade 9 course. 
           Feel free to revisit any chapter or retake the final quiz for additional practice!
          </p>
          </div>)}
        {/* If not all chapters done => show lesson or chapter quiz */}
        {!allChaptersDone && !showFinal && (
          <>
            {!showChapterQuiz ? <LessonUI /> : <ChapterQuizUI />}
          </>
        )}

        {/* If done all chapters => final quiz prompt */}
        {allChaptersDone && !showFinal && (
          <div style={{ textAlign: "center" }}>
            <h2>All Chapters Completed!</h2>
            <p>You can now take the final 50‑question quiz on all topics.</p>
            <button className="lesson-nav-btn" onClick={startFinalQuiz}>
              Start Final Quiz
            </button>
          </div>
        )}

        {/* Final quiz */}
        {showFinal && <FinalQuizUI />}
      </div>
    </div>
  );
}
